import { VisuallyHidden } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FC } from 'react';
import { MBox, MFlex, MText } from '../Monetize';

export type ChatStatusTone = 'idle' | 'thinking' | 'reconnecting';

const IDLE_LABEL = 'AI assistant';

export interface ChatStatusInput {
  isThinking?: boolean;
  isLoading?: boolean;
  isReconnecting?: boolean;
  /** What Monty is doing right now ("Searching contracts"), when known. */
  activityLabel?: string;
}

export interface ChatStatusValue {
  tone: ChatStatusTone;
  label: string;
}

/**
 * Header status under Monty's name. What Monty is doing is announced to
 * screen readers here, since the log holds its announcements while a reply
 * streams; sighted users read it on the activity line in the conversation,
 * so the header shows only a dot while working rather than repeat it.
 * Reconnecting is shown on screen, though: the activity line may already
 * have closed when the connection drops mid-reply. It wins over thinking:
 * while the socket is re-establishing, the turn is stalled regardless of
 * what isThinking says. The socket exposes no "connected" flag, so idle never
 * claims "Online" -- it falls back to the plain role label.
 */
export const getChatStatus = ({
  isThinking,
  isLoading,
  isReconnecting,
  activityLabel,
}: ChatStatusInput): ChatStatusValue => {
  if (isReconnecting) {
    return { tone: 'reconnecting', label: 'Reconnecting…' };
  }
  if (isThinking || isLoading) {
    return {
      tone: 'thinking',
      label: activityLabel ? `${activityLabel}…` : 'Working…',
    };
  }
  return { tone: 'idle', label: IDLE_LABEL };
};

const pulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
`;

const DOT_COLOR_BY_TONE: Record<Exclude<ChatStatusTone, 'idle'>, string> = {
  thinking: 'action.ai',
  reconnecting: 'tOrange.base',
};

export const ChatStatus: FC<ChatStatusInput> = (props) => {
  const { tone, label } = getChatStatus(props);
  const isReconnecting = tone === 'reconnecting';

  return (
    <MFlex alignItems="center" gap={1.5} mt="1px">
      {tone !== 'idle' && (
        <MBox
          boxSize="6px"
          borderRadius="full"
          flexShrink={0}
          bg={DOT_COLOR_BY_TONE[tone]}
          animation={
            tone === 'thinking'
              ? `${pulse} 1.4s ease-in-out infinite`
              : undefined
          }
        />
      )}
      <MText
        fontSize="xs"
        color="fg.muted"
        lineHeight="1.2"
        aria-hidden={isReconnecting}
      >
        {isReconnecting ? label : IDLE_LABEL}
      </MText>
      {/* The conversation log holds its announcements while a reply streams,
          so this is what tells a screen-reader user that Monty is working or
          reconnecting. Empty when idle: the reply itself is the "done" cue. */}
      <VisuallyHidden as="output">
        {tone === 'idle' ? '' : label}
      </VisuallyHidden>
    </MFlex>
  );
};
